import { ChevronDown, ChevronLeft, ChevronRight, ChevronsUpDown, ChevronUp } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui';
import { cn } from '@/lib/cn';
import { TABLE_PAGE_SIZE_OPTIONS, type SortDirection } from '@/lib/use-table-state';

interface SortableHeaderProps {
  label: ReactNode;
  active: boolean;
  direction?: SortDirection | undefined;
  onClick?: (() => void) | undefined;
  className?: string | undefined;
}

/**
 * Column header with built-in sort affordance.
 *
 * - Non-sortable columns: pass the label only.
 * - Sortable columns: pass `onClick` + `active`/`direction` — a chevron
 *   indicates whether the column is the active sort and which way.
 */
export function SortableHeader({
  label,
  active,
  direction,
  onClick,
  className,
}: SortableHeaderProps) {
  if (!onClick) {
    return <th className={cn('px-3 py-2 text-left', className)}>{label}</th>;
  }
  const Icon = active ? (direction === 'asc' ? ChevronUp : ChevronDown) : ChevronsUpDown;
  return (
    <th className={cn('px-3 py-2 text-left', className)}>
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'inline-flex items-center gap-1 text-left transition-colors',
          active ? 'text-brand-900' : 'text-slate-600 hover:text-slate-900',
        )}
      >
        <span>{label}</span>
        <Icon className="h-3 w-3" aria-hidden />
      </button>
    </th>
  );
}

interface TablePagerProps {
  pageIndex: number;
  pageCount: number;
  pageSize: number;
  total: number;
  onPageChange: (next: number) => void;
  onPageSizeChange: (next: number) => void;
}

/**
 * Footer used below studio tables — page navigation + page-size selector
 * + a "showing X–Y of Z" indicator.
 */
export function TablePager({
  pageIndex,
  pageCount,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
}: TablePagerProps) {
  const { t } = useTranslation('common');
  const start = total === 0 ? 0 : pageIndex * pageSize + 1;
  const end = Math.min((pageIndex + 1) * pageSize, total);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-3 py-2 text-small text-slate-600">
      <span>
        {total === 0 ? t('table.empty') : t('table.range', { start, end, total })}
      </span>
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-1">
          <span>{t('table.pageSize')}</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="h-7 rounded border border-slate-300 px-1 text-small"
          >
            {TABLE_PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onPageChange(Math.max(0, pageIndex - 1))}
            disabled={pageIndex === 0}
            aria-label={t('table.previousPage')}
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
          </Button>
          <span className="px-1">
            {t('table.pageOf', { current: pageIndex + 1, total: pageCount })}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onPageChange(Math.min(pageCount - 1, pageIndex + 1))}
            disabled={pageIndex + 1 >= pageCount}
            aria-label={t('table.nextPage')}
          >
            <ChevronRight className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      </div>
    </div>
  );
}
